/*
 * Copyright (C) 2025 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, {useState, useEffect, useRef, useCallback} from 'react'
import Assignment from '@canvas/assignments/backbone/models/Assignment'
import {
  useSettingDependency,
  SETTING_MESSAGES,
} from '@canvas/assignments/react/hooks/useSettingDependency'
import {QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {Checkbox} from '@instructure/ui-checkbox'

/** Sub-assignment for peer reviews with grading info */
export interface PeerReviewSubAssignment {
  points_possible: number
  grading_type: 'points' | 'pass_fail' | string
}

/**
 * Interface for Assignment model methods used in peer review functionality.
 * This provides type safety for the Backbone Assignment model.
 */
export interface AssignmentModel {
  /** Get the group category ID, if any */
  groupCategoryId(): string | null
  /** Check if peer reviews are enabled */
  peerReviews(): boolean
  /** Check if moderated grading is enabled */
  moderatedGrading(): boolean
  /** Get the peer review sub-assignment data */
  peerReviewSubAssignment?(): PeerReviewSubAssignment | null
  /** Get the number of peer reviews required */
  peerReviewCount?(): number
  /** Check if there are existing peer review submissions */
  hasPeerReviewSubmissions?(): boolean
  /** Check if peer reviews can be assigned across sections */
  peerReviewAcrossSections?(): boolean
  /** Check if intra-group peer reviews are enabled */
  intraGroupPeerReviews?(): boolean
  /** Check if peer reviews are anonymous */
  anonymousPeerReviews?(): boolean
  /** Check if submission is required before peer reviews */
  peerReviewSubmissionRequired?(): boolean
}
import {Flex} from '@instructure/ui-flex'
import {Text} from '@instructure/ui-text'
import {View} from '@instructure/ui-view'
import {NumberInput} from '@instructure/ui-number-input'
import {ToggleDetails} from '@instructure/ui-toggle-details'
import {Alert} from '@instructure/ui-alerts'
import {canvasHighContrast, canvas} from '@instructure/ui-themes'
import {ScreenReaderContent} from '@instructure/ui-a11y-content'
import {render, rerender} from '@canvas/react'
import type {Root} from 'react-dom/client'
import {useScope as createI18nScope} from '@canvas/i18n'
import FormattedErrorMessage from '@canvas/assignments/react/FormattedErrorMessage'
import {usePeerReviewSettings} from './hooks/usePeerReviewSettings'
const I18n = createI18nScope('peer_review_details')
const baseTheme = ENV.use_high_contrast ? canvasHighContrast : canvas
const {colors: instui10Colors} = baseTheme
const inputOverride = {mediumHeight: '1.75rem', mediumFontSize: '0.875rem'}
const roots = new Map<string, Root>()
function createOrUpdateRoot(elementId: string, component: React.ReactElement) {
  const container = document.getElementById(elementId)
  if (!container) return
  let root = roots.get(elementId)
  if (!root) {
    root = render(component, container)
    roots.set(elementId, root)
  } else {
    rerender(root, component)
  }
}
const hasValidGroupCategory = (assignment: AssignmentModel): boolean => {
  const groupCategoryId = assignment.groupCategoryId()
  return !!groupCategoryId && groupCategoryId !== 'blank'
}
const getIsGroupAssignment = (assignment: AssignmentModel): boolean => {
  const hasGroupCategoryCheckbox = document.getElementById('has_group_category') as HTMLInputElement
  if (hasGroupCategoryCheckbox) {
    return hasGroupCategoryCheckbox.checked
  }
  return hasValidGroupCategory(assignment)
}
export const renderPeerReviewDetails = (assignment: AssignmentModel) => {
  const $mountPoint = document.getElementById('peer_reviews_allocation_and_grading_details')
  if ($mountPoint) {
    const queryClient = new QueryClient()
    createOrUpdateRoot(
      'peer_reviews_allocation_and_grading_details',
      <QueryClientProvider client={queryClient}>
        <PeerReviewDetails assignment={assignment} />
      </QueryClientProvider>,
    )
  }
}
const FlexRow = ({children, ...props}: {children: React.ReactNode} & any) => (
  <Flex as="div" justifyItems="space-between" wrap="no-wrap" {...props}>
    {children}
  </Flex>
)
const LabeledInput = ({
  label,
  children,
  errorMessage,
  padding = '0',
  inputId,
}: {
  label: string
  children: React.ReactNode
  errorMessage?: string
  padding?: string
  inputId?: string
}) => (
  <>
    <Flex.Item as="div" padding={padding}>
      <FlexRow>
        <Flex.Item as="div" margin="0 0 small large">
          <Text id={inputId ? `${inputId}-label` : undefined} size="contentSmall" weight="bold">
            {label}
          </Text>
        </Flex.Item>
        <Flex.Item as="div" padding="0 small small 0">
          {children}
        </Flex.Item>
      </FlexRow>
    </Flex.Item>
    {errorMessage && (
      <Flex.Item as="div">
        <FormattedErrorMessage message={errorMessage} margin="0 0 x-small large" />
      </Flex.Item>
    )}
  </>
)
const ToggleCheckbox = ({
  testId,
  name,
  checked,
  onChange,
  label,
  id,
}: {
  testId: string
  name: string
  checked: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  label: string
  id: string
}) => (
  <Flex as="div" margin="0 0 0 medium" padding="0 large 0 0" width="100%" justifyItems="end">
    <Flex.Item>
      <Checkbox
        data-testid={testId}
        name={name}
        id={id}
        inline={true}
        variant="toggle"
        checked={checked}
        onChange={onChange}
        label={
          <Flex as="div" width="100%" justifyItems="start">
            <Flex.Item>
              <Text size="contentSmall" weight="bold">
                {label}
              </Text>
            </Flex.Item>
          </Flex>
        }
        labelPlacement="start"
      />
    </Flex.Item>
  </Flex>
)
const SectionHeader = ({title, padding = 'small'}: {title: string; padding?: string}) => (
  <Flex.Item as="div" padding={padding}>
    <Text weight="bold" size="content">
      {title}
    </Text>
  </Flex.Item>
)
const PeerReviewDetails = ({assignment}: {assignment: AssignmentModel}) => {
  const [peerReviewChecked, setPeerReviewChecked] = useState(assignment.peerReviews() || false)
  const [peerReviewEnabled, setPeerReviewEnabled] = useState(!assignment.moderatedGrading())
  const [isGroupAssignment, setIsGroupAssignment] = useState(hasValidGroupCategory(assignment))
  const reviewsRequiredInputRef = useRef<HTMLInputElement | null>(null)
  const pointsPerReviewInputRef = useRef<HTMLInputElement | null>(null)
  const peerReviewSubAssignment = assignment.peerReviewSubAssignment?.()
  const reviewsRequiredCount = assignment.peerReviewCount?.() || 1
  const totalPointsFromDB = peerReviewSubAssignment?.points_possible || 0
  const pointsPerReviewCalculated =
    reviewsRequiredCount > 0 ? totalPointsFromDB / reviewsRequiredCount : 0
  const hasPeerReviewSubmissions =
    ENV.PEER_REVIEW_ALLOCATION_AND_GRADING_ENABLED && assignment.hasPeerReviewSubmissions?.()

  const initialValues = {
    reviewsRequired: reviewsRequiredCount,
    pointsPerReview: pointsPerReviewCalculated,
    totalPoints: totalPointsFromDB,
    allowPeerReviewAcrossMultipleSections: assignment.peerReviewAcrossSections?.() || false,
    allowPeerReviewWithinGroups: assignment.intraGroupPeerReviews?.() || false,
    usePassFailGrading: peerReviewSubAssignment?.grading_type === 'pass_fail',
    anonymousPeerReviews: assignment.anonymousPeerReviews?.() || false,
    submissionRequiredBeforePeerReviews: assignment.peerReviewSubmissionRequired?.() || false,
  }
  const {
    reviewsRequired,
    handleReviewsRequiredChange,
    validateReviewsRequired,
    errorMessageReviewsRequired,
    pointsPerReview,
    handlePointsPerReviewChange,
    validatePointsPerReview,
    errorMessagePointsPerReview,
    totalPoints,
    allowPeerReviewAcrossMultipleSections,
    handleCrossSectionsCheck,
    allowPeerReviewWithinGroups,
    handleInterGroupCheck,
    usePassFailGrading,
    handleUsePassFailCheck,
    anonymousPeerReviews,
    handleAnonymityCheck,
    submissionRequiredBeforePeerReviews,
    handleSubmissionRequiredCheck,
    resetFields,
  } = usePeerReviewSettings(initialValues)
  const validatePeerReviewDetails = useCallback(() => {
    let valid = true
    if (reviewsRequiredInputRef.current) {
      const err = validateReviewsRequired({
        target: reviewsRequiredInputRef.current,
      } as React.FocusEvent<HTMLInputElement>)
      if (err) valid = false
    }
    if (pointsPerReviewInputRef.current) {
      const err = validatePointsPerReview({
        target: pointsPerReviewInputRef.current,
      } as React.FocusEvent<HTMLInputElement>)
      if (err) valid = false
    }
    return valid
  }, [validateReviewsRequired, validatePointsPerReview])
  const focusOnFirstError = useCallback(() => {
    if (reviewsRequiredInputRef.current && errorMessageReviewsRequired) {
      reviewsRequiredInputRef.current.focus()
    } else if (pointsPerReviewInputRef.current && errorMessagePointsPerReview) {
      pointsPerReviewInputRef.current.focus()
    }
  }, [errorMessageReviewsRequired, errorMessagePointsPerReview])
  const handleMouseOut = (e: React.MouseEvent<HTMLDivElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement
    const currentTarget = e.currentTarget
    // Does not trigger validation if relatedTarget is within the peer review section
    if (relatedTarget && currentTarget.contains(relatedTarget)) {
      return
    }
    validatePeerReviewDetails()
  }
  useSettingDependency(SETTING_MESSAGES.TOGGLE_PEER_REVIEWS, {
    onDisabled: () => {
      setPeerReviewEnabled(false)
      setPeerReviewChecked(false)
    },
    onEnabled: () => {
      setPeerReviewEnabled(true)
    },
  })
  useEffect(() => {
    const handleGroupCategoryChange = () => {
      setIsGroupAssignment(getIsGroupAssignment(assignment))
    }
    document.addEventListener('group_category_changed', handleGroupCategoryChange)
    return () => {
      document.removeEventListener('group_category_changed', handleGroupCategoryChange)
    }
  }, [assignment])
  useEffect(() => {
    const mountPoint = document.getElementById('peer_reviews_allocation_and_grading_details')
    if (mountPoint) {
      ;(mountPoint as any).validatePeerReviewDetails = validatePeerReviewDetails
      ;(mountPoint as any).focusOnFirstError = focusOnFirstError
    }
    return () => {
      if (mountPoint) {
        delete (mountPoint as any).validatePeerReviewDetails
        delete (mountPoint as any).focusOnFirstError
      }
    }
  }, [validatePeerReviewDetails, focusOnFirstError])
  const handlePeerReviewCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPeerReviewChecked(e.target.checked)
    if (!e.target.checked) {
      resetFields()
    }
  }
  const advancedConfigLabel = (
    <Text size="content">{I18n.t('Advanced Peer Review Configurations')}</Text>
  )
  return (
    <Flex as="div" direction="column" width="100%">
      {/* Hidden inputs to preserve values when Advanced Configuration is collapsed or peer reviews disabled */}
      <Flex.Item>
        <input
          type="hidden"
          id="assignment_peer_reviews_hidden"
          name="peer_reviews_hidden"
          value={peerReviewChecked ? 'true' : 'false'}
        />
        <input
          type="hidden"
          id="peer_reviews_across_sections_checkbox_hidden"
          name="peer_reviews_across_sections_hidden"
          value={peerReviewChecked && allowPeerReviewAcrossMultipleSections ? 'true' : 'false'}
        />
        <input
          type="hidden"
          id="peer_reviews_within_groups_checkbox_hidden"
          name="intra_group_peer_reviews_hidden"
          value={
            peerReviewChecked && isGroupAssignment && allowPeerReviewWithinGroups ? 'true' : 'false'
          }
        />
        <input
          type="hidden"
          id="peer_reviews_pass_fail_grading_checkbox_hidden"
          name="peer_reviews_pass_fail_grading_hidden"
          value={peerReviewChecked && usePassFailGrading ? 'true' : 'false'}
        />
        <input
          type="hidden"
          id="peer_reviews_anonymity_checkbox_hidden"
          name="anonymous_peer_reviews_hidden"
          value={peerReviewChecked && anonymousPeerReviews ? 'true' : 'false'}
        />
        <input
          type="hidden"
          id="peer_reviews_submission_required_checkbox_hidden"
          name="peer_review_submission_required_hidden"
          value={peerReviewChecked && submissionRequiredBeforePeerReviews ? 'true' : 'false'}
        />
        <input
          type="hidden"
          id="assignment_peer_reviews_count_hidden"
          name="peer_review_count_hidden"
          value={peerReviewChecked ? reviewsRequired : '0'}
        />
        <input
          type="hidden"
          id="assignment_peer_reviews_max_input_hidden"
          name="peer_review_points_per_review_hidden"
          value={peerReviewChecked ? pointsPerReview : '0'}
        />
      </Flex.Item>
      <Flex.Item>
        <Flex direction="column" padding="medium 0 medium x-small">
          <Flex.Item as="div" padding="xx-small">
            <Checkbox
              id="assignment_peer_reviews_checkbox"
              name="peer_reviews"
              checked={peerReviewChecked}
              disabled={!peerReviewEnabled || hasPeerReviewSubmissions}
              onChange={handlePeerReviewCheck}
              label={I18n.t('Require Peer Reviews')}
              size="small"
              themeOverride={{
                checkedBackground: instui10Colors.dataVisualization.ocean40Secondary,
                checkedBorderColor: 'white',
              }}
              data-testid="peer-review-checkbox"
            />
          </Flex.Item>
        </Flex>
      </Flex.Item>
      {!peerReviewEnabled && (
        <Flex.Item as="div" padding="0 0 xx-small medium">
          <View as="div" margin="0 0 0 xx-small">
            <Text size="contentSmall">
              {I18n.t('Peer reviews cannot be enabled for assignments with moderated grading.')}
            </Text>
          </View>
        </Flex.Item>
      )}
      {peerReviewChecked && (
        // There is no need to set onBlur handler on the parent div element since those events are handled in the TextInput components
        /* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */
        <div onMouseOut={handleMouseOut}>
          <SectionHeader title={I18n.t('Review Settings')} padding="none small small small" />
          <LabeledInput
            label={I18n.t('Reviews Required*')}
            padding="x-small 0 0 0"
            errorMessage={errorMessageReviewsRequired}
            inputId="assignment_peer_reviews_count"
          >
            <NumberInput
              id="assignment_peer_reviews_count"
              name="peer_review_count"
              data-testid="reviews-required-input"
              renderLabel={<ScreenReaderContent>{I18n.t('Reviews Required')}</ScreenReaderContent>}
              width="4.5rem"
              showArrows={false}
              allowStringValue
              textAlign="start"
              onChange={handleReviewsRequiredChange}
              onBlur={validateReviewsRequired}
              themeOverride={inputOverride}
              value={reviewsRequired}
              interaction={hasPeerReviewSubmissions ? 'disabled' : 'enabled'}
              inputRef={(el: HTMLInputElement | null) => {
                reviewsRequiredInputRef.current = el
                if (el) el.style.marginLeft = '0'
              }}
            />
          </LabeledInput>
          <LabeledInput
            label={I18n.t('Points per Peer Review')}
            padding="x-small 0 0 0"
            errorMessage={errorMessagePointsPerReview}
            inputId="assignment_peer_reviews_max_input"
          >
            <NumberInput
              id="assignment_peer_reviews_max_input"
              data-testid="points-per-review-input"
              renderLabel={
                <ScreenReaderContent>{I18n.t('Points per Peer Review')}</ScreenReaderContent>
              }
              width="4.5rem"
              showArrows={false}
              allowStringValue
              textAlign="start"
              inputMode="decimal"
              onChange={handlePointsPerReviewChange}
              onBlur={validatePointsPerReview}
              themeOverride={inputOverride}
              value={pointsPerReview}
              interaction={hasPeerReviewSubmissions ? 'disabled' : 'enabled'}
              inputRef={(el: HTMLInputElement | null) => {
                pointsPerReviewInputRef.current = el
                if (el) el.style.marginLeft = '0'
              }}
            />
          </LabeledInput>
          <Flex.Item as="div" padding="x-small 0 small 0">
            <FlexRow>
              <Flex.Item as="div" margin="0 0 0 large">
                <Text size="contentSmall" weight="bold">
                  {I18n.t('Total Points for Peer Review(s)')}
                </Text>
              </Flex.Item>
              <Flex.Item as="div" padding="0 small 0 0">
                <View as="div" padding="0 x-small 0 0">
                  <Text size="contentSmall" weight="bold" data-testid="total-peer-review-points">
                    {totalPoints}
                  </Text>
                </View>
              </Flex.Item>
            </FlexRow>
          </Flex.Item>

          {hasPeerReviewSubmissions && (
            <Flex.Item as="div" padding="small">
              <Alert variant="warning">
                {I18n.t(
                  'Students have already submitted peer reviews, so peer reviews cannot be disabled and reviews required and points cannot be changed.',
                )}
              </Alert>
            </Flex.Item>
          )}

          <Flex.Item as="div" padding="small">
            <ToggleDetails
              summary={advancedConfigLabel}
              themeOverride={{
                togglePadding: '0',
              }}
            >
              <Flex direction="column">
                <hr style={{margin: '0.5rem 0 1rem'}} aria-hidden="true"></hr>
                <SectionHeader title={I18n.t('Allocations')} padding="0" />
                <Flex.Item as="div" overflowY="visible" margin="small 0">
                  <ToggleCheckbox
                    testId="across-sections-checkbox"
                    name="peer_reviews_across_sections"
                    id="peer_reviews_across_sections_checkbox"
                    checked={allowPeerReviewAcrossMultipleSections}
                    onChange={handleCrossSectionsCheck}
                    label={I18n.t('Allow peer reviews across sections')}
                  />
                </Flex.Item>
                {isGroupAssignment && (
                  <Flex.Item as="div" overflowY="visible">
                    <ToggleCheckbox
                      testId="within-groups-checkbox"
                      name="peer_reviews_prevent_friends"
                      id="peer_reviews_within_groups_checkbox"
                      checked={allowPeerReviewWithinGroups}
                      onChange={handleInterGroupCheck}
                      label={I18n.t('Allow peer reviews within groups')}
                    />
                  </Flex.Item>
                )}
                <SectionHeader title={I18n.t('Grading')} padding="small 0 0 0" />
                <Flex.Item overflowY="visible" margin="small 0">
                  <ToggleCheckbox
                    testId="pass-fail-grading-checkbox"
                    name="peer_reviews_manual_grading"
                    id="peer_reviews_pass_fail_grading_checkbox"
                    checked={usePassFailGrading}
                    onChange={handleUsePassFailCheck}
                    label={I18n.t('Use complete/incomplete instead of points for grading')}
                  />
                </Flex.Item>
                <SectionHeader title={I18n.t('Anonymity')} padding="medium 0 0 0" />
                <Flex.Item overflowY="visible" margin="small 0">
                  <ToggleCheckbox
                    testId="anonymity-checkbox"
                    name="peer_reviews_hide_reviewer_names"
                    id="peer_reviews_anonymity_checkbox"
                    checked={anonymousPeerReviews}
                    onChange={handleAnonymityCheck}
                    label={I18n.t('Reviewers do not see who they review')}
                  />
                </Flex.Item>
                <SectionHeader title={I18n.t('Submission required')} padding="medium 0 0 0" />
                <Flex.Item overflowY="visible" margin="small 0">
                  <ToggleCheckbox
                    testId="submission-required-checkbox"
                    name="peer_reviews_submission_required"
                    id="peer_reviews_submission_required_checkbox"
                    checked={submissionRequiredBeforePeerReviews}
                    onChange={handleSubmissionRequiredCheck}
                    label={I18n.t(
                      'Reviewers must submit their assignment before they can be allocated reviews',
                    )}
                  />
                </Flex.Item>
              </Flex>
            </ToggleDetails>
          </Flex.Item>
        </div>
      )}
    </Flex>
  )
}
export default PeerReviewDetails
